import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import AdminShell from "@/components/AdminShell";
import { Eyebrow } from "@/components/brand";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminTestChat() {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [conversationId, setConversationId] = useState(null);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();

  const token = localStorage.getItem("token");

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || loading) return;

    const userMessage = { role: "user", content: inputMessage, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage("");
    setLoading(true);

    try {
      const config = { withCredentials: true };
      if (token) {
        config.headers = { Authorization: `Bearer ${token}` };
      }

      const response = await axios.post(
        `${API}/chat/message`,
        {
          message: inputMessage,
          conversation_id: conversationId
        },
        config
      );

      const { conversation_id, message, sources } = response.data;
      setConversationId(conversation_id);
      
      const assistantMessage = {
        ...message,
        sources: sources || []
      };
      
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send message");
    } finally {
      setLoading(false);
    }
  };

  const handleClearChat = () => {
    setMessages([]);
    setConversationId(null);
    toast.success("Chat cleared - starting fresh test");
  };

  return (
    <AdminShell active="test-chat" fill>
      {/* Header */}
      <div className="bg-white border-b border-line px-5 md:px-7 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} data-testid="back-to-dashboard">
            ← Back
          </Button>
          <div>
            <h1 className="text-base font-bold text-ink tracking-tight">Admin Test Chat</h1>
            <p className="text-xs text-mute">Test AI responses before clients see them</p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleClearChat}
          data-testid="clear-chat-button"
        >
          Clear Chat
        </Button>
      </div>

      {/* Info Banner */}
      <div className="bg-cream border-b border-[#e6dfd0] px-5 md:px-7 py-2.5">
        <p className="text-xs text-ink-2 leading-relaxed">
          <strong className="text-ink">Testing Mode:</strong> This chat helps you verify AI responses. Sources will be shown for each answer. This conversation is NOT saved or shared.
        </p>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-hidden">
        <ScrollArea className="h-full px-5 md:px-7 py-6">
          <div className="max-w-4xl mx-auto space-y-4">
            {messages.length === 0 && (
              <div className="py-10 animate-rise-in" data-testid="welcome-message">
                <Eyebrow className="mb-3">Testing sandbox</Eyebrow>
                <h2 className="text-3xl font-extrabold text-ink tracking-tight mb-2">Admin Test Chat</h2>
                <p className="text-mute mb-6 max-w-xl">Ask questions to test if the AI provides correct answers from your uploaded content.</p>
                <div className="bg-warm border border-[#f3d9b0] rounded-xl p-5 max-w-md">
                  <h3 className="font-semibold text-[#7a4b0a] mb-2 text-sm">Testing Tips:</h3>
                  <ul className="text-sm text-[#7a4b0a] space-y-1.5">
                    <li>• Ask questions you KNOW are in your uploaded videos</li>
                    <li>• Check if sources are cited correctly</li>
                    <li>• Ask questions NOT in your content - AI should say it doesn't know</li>
                    <li>• Verify accuracy before letting clients use the system</li>
                  </ul>
                </div>
              </div>
            )}

            {messages.map((msg, idx) => (
              <div key={msg.timestamp || idx} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"} message-enter`}>
                <div className={`max-w-3xl px-5 py-3.5 ${
                  msg.role === "user" 
                    ? "bg-ink text-white rounded-[14px] rounded-br-[4px]" 
                    : "bg-white text-ink rounded-[14px] rounded-bl-[4px] border border-line"
                }`}>
                  <div className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</div>
                  
                  {msg.sources && msg.sources.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-line">
                      <p className="th-mono text-equal-dark mb-1.5">✓ Sources Found:</p>
                      <div className="space-y-1">
                        {msg.sources.map((source, i) => (
                          <div key={`src-${i}`} className="text-xs text-ink-2 flex items-center gap-2 flex-wrap">
                            <span>📄</span>
                            <span className="font-medium">{typeof source === 'string' ? source : source.filename}</span>
                            {source.timestamp && (
                              <button
                                onClick={() => {
                                  toast.info(`This information is from ${source.filename} at timestamp ${source.timestamp}`, {
                                    description: "Video source reference",
                                    duration: 4000
                                  });
                                }}
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-mint text-equal-dark border border-[#bfe5d1] rounded-md font-mono hover:bg-equal hover:text-white transition-colors cursor-pointer"
                                title={`Click to see source details: ${source.filename} at ${source.timestamp}`}
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                ⏱️ {source.timestamp}
                              </button>
                            )}
                            {source.page_number && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-cream text-ink-2 border border-[#e6dfd0] rounded-md font-mono">
                                📄 Page {source.page_number}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {msg.role === "assistant" && (!msg.sources || msg.sources.length === 0) && (
                    <div className="mt-3 pt-3 border-t border-[#f2c1bb]">
                      <p className="text-xs font-semibold text-[#c0392b]">⚠️ No sources found - AI may not have relevant content</p>
                    </div>
                  )}
                  
                  <p className={`text-[11px] font-mono mt-2 ${msg.role === "user" ? "text-white/60" : "text-mute"}`}>
                    {new Date(msg.timestamp).toLocaleTimeString()}
                  </p>
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-white rounded-[14px] rounded-bl-[4px] border border-line px-5 py-4">
                  <div className="flex gap-1.5">
                    <div className="w-1.5 h-1.5 bg-equal rounded-full animate-bounce"></div>
                    <div className="w-1.5 h-1.5 bg-equal rounded-full animate-bounce" style={{ animationDelay: "0.1s" }}></div>
                    <div className="w-1.5 h-1.5 bg-equal rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>
      </div>

      {/* Input Area */}
      <div className="bg-white border-t border-line px-5 md:px-7 py-4">
        <div className="max-w-4xl mx-auto">
          <form onSubmit={handleSendMessage} className="flex gap-3">
            <Input
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Ask a test question..."
              className="flex-1 h-12 px-4"
              disabled={loading}
              data-testid="message-input"
            />
            <Button
              type="submit"
              className="px-6 h-12"
              disabled={loading || !inputMessage.trim()}
              data-testid="send-message-button"
            >
              Send Test
            </Button>
          </form>
        </div>
      </div>
    </AdminShell>
  );
}
